// This file was auto-generated by Fern from our API Definition.
import { normalizeClientOptionsWithAuth } from "../../../../BaseClient.js";
import { mergeHeaders } from "../../../../core/headers.js";
import * as core from "../../../../core/index.js";
import { toJson } from "../../../../core/json.js";
import * as environments from "../../../../environments.js";
import { handleNonStatusCodeError } from "../../../../errors/handleNonStatusCodeError.js";
import * as errors from "../../../../errors/index.js";
import * as AgentaApi from "../../../index.js";
/**
 * Ingest and query traces, spans, and metrics from running applications.
 */
export class TracesClient {
    _options;
    constructor(options) {
        this._options = normalizeClientOptionsWithAuth(options);
    }
    /**
     * Fetch multiple traces by known IDs.
     *
     * Point lookup endpoint. Accepts either repeated query params
     * (`?trace_id=a&trace_id=b`) or a comma-separated single param
     * (`?trace_ids=a,b`). Results are deduplicated. Returns `400` when
     * no IDs are supplied. Use `POST /traces/query` for filter-based
     * retrieval.
     *
     * @param {AgentaApi.FetchTracesRequest} request
     * @param {TracesClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.traces.fetchTraces()
     */
    fetchTraces(request = {}, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__fetchTraces(request, requestOptions));
    }
    async __fetchTraces(request = {}, requestOptions) {
        const { trace_id: traceId, trace_ids: traceIds } = request;
        const _queryParams = {
            trace_id: traceId !== undefined ? toJson(traceId) : undefined,
            trace_ids: traceIds,
        };
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "traces/"),
            method: "GET",
            headers: _headers,
            queryParameters: { ..._queryParams, ...requestOptions?.queryParams },
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/traces/");
    }
    /**
     * Create a single trace from the canonical `Trace` shape.
     *
     * Accepts one trace (`trace_id` plus a nested `spans` tree) and
     * returns the resulting `trace_id`. The payload is internally
     * normalized into the same ingest pipeline as
     * `POST /tracing/spans/ingest`.
     *
     * Returns `202 Accepted`. The async write contract applies — see
     * [Tracing — Async write
     * contract](/reference/api-guide/tracing#async-write-contract-202).
     *
     * Use this when you want to operate on whole traces in the
     * list-shaped `Trace` payload. For flat-list ingestion or multiple
     * traces in one call, use `POST /traces/ingest` (plural).
     *
     * @param {AgentaApi.TraceRequest} request
     * @param {TracesClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.traces.createTrace({})
     */
    createTrace(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__createTrace(request, requestOptions));
    }
    async __createTrace(request, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "traces/"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/traces/");
    }
    /**
     * Query traces as a list of canonical `Trace` records.
     *
     * Thin wrapper over the shared span-query backend that forces
     * `focus = "trace"` and returns the list-shaped `Traces` payload
     * (one entry per trace, each with its nested `spans` tree). Use this
     * to build a table of runs, where each row is a trace.
     *
     * ## Request body
     *
     * - `filtering` — span-level conditions, same dialect as
     *   `POST /spans/query`. A trace matches when any of its spans
     *   matches.
     * - `windowing` — cursor pagination and time range.
     * - `query_ref`, `query_variant_ref`, `query_revision_ref` — resolve
     *   filters and windowing from a saved query revision. If the
     *   revision's stored `formatting.focus` is `span`, this endpoint
     *   returns `409` — call `POST /spans/query` instead.
     *
     * ## Response
     *
     * Returns `{count, traces: [...], windowing}`. One response holds at most
     * one page: 100 traces when `windowing.limit` is not set, and never more
     * than 1000 (the server settings `AGENTA_API_TRACING_QUERY_DEFAULT_LIMIT`
     * and `AGENTA_API_TRACING_QUERY_MAX_LIMIT`). `windowing` is present when
     * the page is full: send it as `windowing` to read the next page. A
     * trace with matching spans on both sides of a page boundary can appear
     * on both pages; deduplicate by `trace_id`. A query that filters only on
     * `trace_id` returns those traces whole, with no limit.
     *
     * Span `attributes` leave out `ag.data.parameters.agent.skills` (the
     * skill text of an agent run), except when the query filters only on
     * `trace_id`. `GET /traces/{trace_id}` returns it too.
     *
     * For the per-trace map shape
     * keyed by `trace_id`, call `POST /tracing/spans/query` with
     * `focus="trace"`.
     *
     * @param {AgentaApi.TracesQueryRequest} request
     * @param {TracesClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.traces.queryTraces()
     */
    queryTraces(request = {}, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__queryTraces(request, requestOptions));
    }
    async __queryTraces(request = {}, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "traces/query"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/traces/query");
    }
    /**
     * Fetch a single trace by `trace_id` in the canonical `Trace` shape.
     *
     * Returns `{count: 1, trace}` when found and `{count: 0}` otherwise.
     * `trace_id` must be a 32-char hex UUID; any other format returns
     * `400`. The reserved path segments `query` and `ingest` return
     * `405` to disambiguate from the sibling query/ingest endpoints.
     *
     * @param {AgentaApi.FetchTraceRequest} request
     * @param {TracesClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.traces.fetchTrace({
     *         trace_id: "trace_id"
     *     })
     */
    fetchTrace(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__fetchTrace(request, requestOptions));
    }
    async __fetchTrace(request, requestOptions) {
        const { trace_id: traceId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `traces/${core.url.encodePathParam(traceId)}`),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/traces/{trace_id}");
    }
    /**
     * Replace a trace's spans using the canonical `Trace` shape.
     *
     * Path `trace_id` must match the `trace_id` inside the payload's
     * `trace.trace_id`. Mismatches return `400`. The payload must
     * describe exactly one trace.
     *
     * Edit re-ingests the spans through the same stream as
     * `POST /tracing/spans/ingest`. Returns `202 Accepted` once the
     * spans are queued. The worker reconciles the trace asynchronously.
     *
     * @param {AgentaApi.EditTraceRequest} request
     * @param {TracesClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.traces.editTrace({
     *         trace_id: "trace_id",
     *         body: {}
     *     })
     */
    editTrace(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__editTrace(request, requestOptions));
    }
    async __editTrace(request, requestOptions) {
        const { trace_id: traceId, body: _body } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `traces/${core.url.encodePathParam(traceId)}`),
            method: "PUT",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: _body,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "PUT", "/traces/{trace_id}");
    }
    /**
     * @param {AgentaApi.DeleteTraceRequest} request
     * @param {TracesClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.traces.deleteTrace({
     *         trace_id: "trace_id"
     *     })
     */
    deleteTrace(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__deleteTrace(request, requestOptions));
    }
    async __deleteTrace(request, requestOptions) {
        const { trace_id: traceId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `traces/${core.url.encodePathParam(traceId)}`),
            method: "DELETE",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "DELETE", "/traces/{trace_id}");
    }
    /**
     * Fetch spans by known IDs.
     *
     * Point lookup endpoint. At least one of `trace_id` or `span_id`
     * must be present. Both accept either repeated query params
     * (`?trace_id=a&trace_id=b`) or a comma-separated single param
     * (`?trace_ids=a,b`); results are deduplicated.
     *
     * Returns `400` when neither IDs nor trace IDs are supplied.
     * For filter-based retrieval, use `POST /spans/query`.
     *
     * @param {AgentaApi.FetchSpansRequest} request
     * @param {TracesClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.traces.fetchSpans()
     */
    fetchSpans(request = {}, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__fetchSpans(request, requestOptions));
    }
    async __fetchSpans(request = {}, requestOptions) {
        const { trace_id: traceId, trace_ids: traceIds, span_id: spanId, span_ids: spanIds } = request;
        const _queryParams = {
            trace_id: traceId !== undefined ? toJson(traceId) : undefined,
            trace_ids: traceIds,
            span_id: spanId !== undefined ? toJson(spanId) : undefined,
            span_ids: spanIds,
        };
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "spans/"),
            method: "GET",
            headers: _headers,
            queryParameters: { ..._queryParams, ...requestOptions?.queryParams },
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/spans/");
    }
    /**
     * Query spans as a flat list.
     *
     * Thin wrapper over the shared span-query backend that forces
     * `focus = "span"`. Use this when you want a paged list of spans
     * regardless of trace hierarchy — for example, to surface all LLM
     * calls across traces or to stream spans into an external system.
     *
     * ## Request body
     *
     * - `filtering` — span-level conditions (fields on `Span` and
     *   `attributes` paths).
     * - `windowing` — cursor pagination and time range (see
     *   [Query Pattern](/reference/api-guide/query-pattern#windowing)).
     * - `query_ref`, `query_variant_ref`, `query_revision_ref` — resolve
     *   filtering and windowing from a saved query revision. If the
     *   revision's stored `formatting.focus` is `trace`, this endpoint
     *   returns `409` — call `POST /traces/query` for that revision.
     *
     * ## Response
     *
     * Returns `{count, spans, windowing}`. One response holds at most one
     * page: 100 spans when `windowing.limit` is not set, and never more than
     * 1000 (the server settings `AGENTA_API_TRACING_QUERY_DEFAULT_LIMIT` and
     * `AGENTA_API_TRACING_QUERY_MAX_LIMIT`). `windowing` is present when the page
     * is full: send it as `windowing` to read the next page.
     *
     * Span `attributes` leave out `ag.data.parameters.agent.skills` (the
     * skill text of an agent run). `GET /spans/{trace_id}/{span_id}`,
     * `GET /traces/{trace_id}` and a query that filters only on `trace_id`
     * return it.
     *
     * For the nested per-trace shape, call
     * `POST /traces/query` or `POST /tracing/spans/query` with
     * `focus="trace"` instead.
     *
     * @param {AgentaApi.SpansQueryRequest} request
     * @param {TracesClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.traces.querySpans()
     */
    querySpans(request = {}, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__querySpans(request, requestOptions));
    }
    async __querySpans(request = {}, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "spans/query"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/spans/query");
    }
    /**
     * @param {AgentaApi.QuerySpansAnalyticsRequest} request
     * @param {TracesClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     * @throws {@link AgentaApi.GatewayTimeoutError}
     *
     * @example
     *     await client.traces.querySpansAnalytics()
     */
    querySpansAnalytics(request = {}, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__querySpansAnalytics(request, requestOptions));
    }
    async __querySpansAnalytics(request = {}, requestOptions) {
        const { focus, format, oldest, newest, interval, rate, filter, specs } = request;
        const _queryParams = {
            focus: focus !== undefined ? focus : undefined,
            format: format !== undefined ? format : undefined,
            oldest: oldest !== undefined ? (typeof oldest === "string" ? oldest : toJson(oldest)) : undefined,
            newest: newest !== undefined ? (typeof newest === "string" ? newest : toJson(newest)) : undefined,
            interval,
            rate,
            filter,
            specs,
        };
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "spans/analytics/query"),
            method: "POST",
            headers: _headers,
            queryParameters: { ..._queryParams, ...requestOptions?.queryParams },
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                case 504:
                    throw new AgentaApi.GatewayTimeoutError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/spans/analytics/query");
    }
    /**
     * @param {AgentaApi.SessionsQueryRequest} request
     * @param {TracesClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.traces.querySpansSessions()
     */
    querySpansSessions(request = {}, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__querySpansSessions(request, requestOptions));
    }
    async __querySpansSessions(request = {}, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "spans/sessions/query"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/spans/sessions/query");
    }
    /**
     * @param {AgentaApi.UsersQueryRequest} request
     * @param {TracesClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.traces.querySpansUsers()
     */
    querySpansUsers(request = {}, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__querySpansUsers(request, requestOptions));
    }
    async __querySpansUsers(request = {}, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "spans/users/query"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/spans/users/query");
    }
    /**
     * Fetch a single span by `trace_id` + `span_id`.
     *
     * Returns `{count: 1, span}` when found and `{count: 0}` otherwise.
     * Both IDs are required path parameters. Use this to drill in on one
     * span from a trace waterfall without pulling the full tree.
     *
     * @param {AgentaApi.FetchSpanRequest} request
     * @param {TracesClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.traces.fetchSpan({
     *         trace_id: "trace_id",
     *         span_id: "span_id"
     *     })
     */
    fetchSpan(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__fetchSpan(request, requestOptions));
    }
    async __fetchSpan(request, requestOptions) {
        const { trace_id: traceId, span_id: spanId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `spans/${core.url.encodePathParam(traceId)}/${core.url.encodePathParam(spanId)}`),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/spans/{trace_id}/{span_id}");
    }
    /**
     * Create a single-span "simple" trace.
     *
     * This endpoint is a higher-level helper for the common case of
     * recording one self-contained event — an evaluator output, a human
     * annotation, a feedback entry, a manually-logged inference. It
     * creates one span under a fresh `trace_id` and returns the resulting
     * handle.
     *
     * ## When to use this vs. `/tracing/spans/ingest`
     *
     * - **Use this endpoint** when you have a single payload to record
     *   with no internal hierarchy: evaluation results, human feedback,
     *   manual annotations, or a standalone completion. It takes care of
     *   `trace_id`/`span_id` generation, attribute namespacing, and link
     *   wiring for you.
     * - **Use `POST /tracing/spans/ingest`** when you need multi-span
     *   traces (e.g. an agent run with nested tool calls and LLM spans),
     *   precise control over IDs, timings, or parent/child relationships,
     *   or when forwarding traces from another OTel-compatible source.
     *
     * ## Request body
     *
     * Send a `trace` object with:
     *
     * - `origin` — who produced the trace (`human`, `auto`, `custom`).
     * - `kind` — intent (`adhoc`, `eval`, `play`).
     * - `channel` — transport that produced it (`sdk`, `api`, `web`, `otlp`).
     * - `data` — required dict carrying the actual payload (inputs,
     *   outputs, or evaluator results).
     * - `tags`, `meta` — optional free-form dicts for filtering and
     *   metadata.
     * - `references` — optional links to Agenta entities (application,
     *   variant, revision, evaluator, testset, etc.).
     * - `links` — optional OTel-style links to other traces/spans.
     *
     * Use `PATCH /preview/tracing/traces/{trace_id}` to update fields
     * later, `GET` to fetch, and `DELETE` to remove. See
     * [Tracing — References and links](/reference/api-guide/tracing#references-and-entity-linking)
     * for when to use `references` vs. `links`.
     *
     * @param {AgentaApi.SimpleTraceCreateRequest} request
     * @param {TracesClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.traces.createSimpleTrace({
     *         trace: {
     *             data: {},
     *             references: {},
     *             links: {
     *                 "key": {}
     *             }
     *         }
     *     })
     */
    createSimpleTrace(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__createSimpleTrace(request, requestOptions));
    }
    async __createSimpleTrace(request, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "simple/traces/"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/simple/traces/");
    }
    /**
     * Fetch a single "simple" trace by `trace_id`.
     *
     * Returns the high-level `SimpleTrace` view (origin, kind, channel,
     * data, references, links) rather than the raw OTel span shape. Use
     * this for evaluation results, feedback entries, and annotations
     * created via `POST /simple/traces/`. For the span-level view of the
     * same trace, call `GET /tracing/traces/{trace_id}`.
     *
     * @param {AgentaApi.FetchSimpleTraceRequest} request
     * @param {TracesClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.traces.fetchSimpleTrace({
     *         trace_id: "trace_id"
     *     })
     */
    fetchSimpleTrace(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__fetchSimpleTrace(request, requestOptions));
    }
    async __fetchSimpleTrace(request, requestOptions) {
        const { trace_id: traceId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `simple/traces/${core.url.encodePathParam(traceId)}`),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/simple/traces/{trace_id}");
    }
    /**
     * Delete a "simple" trace.
     *
     * Removes the single-span trace created via
     * `POST /simple/traces/`. Returns the `(trace_id, span_id)` pair
     * that was removed, for logging or downstream cleanup. Use
     * `DELETE /tracing/traces/{trace_id}` when operating on a
     * multi-span trace.
     *
     * @param {AgentaApi.DeleteSimpleTraceRequest} request
     * @param {TracesClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.traces.deleteSimpleTrace({
     *         trace_id: "trace_id"
     *     })
     */
    deleteSimpleTrace(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__deleteSimpleTrace(request, requestOptions));
    }
    async __deleteSimpleTrace(request, requestOptions) {
        const { trace_id: traceId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `simple/traces/${core.url.encodePathParam(traceId)}`),
            method: "DELETE",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "DELETE", "/simple/traces/{trace_id}");
    }
    /**
     * Update an existing "simple" trace.
     *
     * Supplied fields overwrite the existing trace. Fields not present
     * in the request body are left unchanged. `data` is required (the
     * payload being recorded); `tags`, `meta`, `references`, and
     * `links` are optional.
     *
     * This endpoint is intended for annotations and feedback entries,
     * where the `data.outputs` is the part that typically gets revised.
     * For span-level edits, use `PUT /tracing/traces/{trace_id}`.
     *
     * @param {AgentaApi.SimpleTraceEditRequest} request
     * @param {TracesClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.traces.editSimpleTrace({
     *         trace_id: "trace_id",
     *         trace: {
     *             data: {}
     *         }
     *     })
     */
    editSimpleTrace(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__editSimpleTrace(request, requestOptions));
    }
    async __editSimpleTrace(request, requestOptions) {
        const { trace_id: traceId, ..._body } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `simple/traces/${core.url.encodePathParam(traceId)}`),
            method: "PATCH",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: _body,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "PATCH", "/simple/traces/{trace_id}");
    }
    /**
     * Query "simple" traces.
     *
     * Filter annotations and feedback by `origin`, `kind`, `channel`,
     * `tags`, `meta`, `references`, and `links`. The shape of the
     * request body is described in the
     * [Simple Endpoints](/reference/api-guide/simple-endpoints#query-traces)
     * guide, including the distinction between filtering via
     * `trace.links` (inbound links on the trace) and the top-level
     * `links` (batch GET by the trace's own IDs).
     *
     * Use this endpoint when building feedback or annotation UIs.
     * For span-level queries across all trace types, use
     * `POST /tracing/spans/query`.
     *
     * @param {AgentaApi.SimpleTraceQueryRequest} request
     * @param {TracesClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.traces.querySimpleTraces()
     */
    querySimpleTraces(request = {}, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__querySimpleTraces(request, requestOptions));
    }
    async __querySimpleTraces(request = {}, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "simple/traces/query"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/simple/traces/query");
    }
}
